/**
 * Notify Step Executor — Multi-channel notifications as a workflow step
 *
 * Config:
 * - channel: 'inApp' | 'email' | 'telegram'
 * - message: string
 * - recipient?: string (userId, email, or chatId)
 * - subject?: string (for email)
 * - title?: string (for inApp)
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor, InAppNotification } from '../types';

export const notifyStepExecutor: StepExecutor = {
  type: 'notify',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    try {
      const channel = (step.config.channel as string) || 'inApp';
      const message = step.config.message as string || '';
      const recipient = step.config.recipient as string || '';

      switch (channel) {
        case 'inApp': {
          const title = (step.config.title as string) || step.name;
          const notification: InAppNotification = {
            id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: (step.config.notifType as InAppNotification['type']) || 'new_message',
            title,
            message,
            entityType: context.triggerEvent.entityType,
            entityId: context.triggerEvent.entityId,
            read: false,
            createdAt: new Date().toISOString(),
            module: step.config.module as string,
            actionUrl: step.config.actionUrl as string,
          };

          // Store in pipelineData for other steps to consume
          return {
            success: true,
            output: { notification, notifyChannel: 'inApp' },
          };
        }

        case 'email': {
          // Lazy import to avoid circular dependencies
          const { imapClient } = await import('../../../services/imapClient');
          const subject = (step.config.subject as string) || 'Notification';

          await imapClient.sendEmail({
            to: [recipient],
            subject,
            body: message,
          });

          return {
            success: true,
            output: { notifyChannel: 'email', recipient, subject },
          };
        }

        case 'telegram': {
          const { telegramService } = await import('../../../services/telegramService');
          const chatId = recipient || (step.config.chatId as string);

          if (!chatId) {
            return { success: false, error: 'Telegram chatId or recipient required' };
          }

          await telegramService.sendMessage(chatId, message);

          return {
            success: true,
            output: { notifyChannel: 'telegram', chatId },
          };
        }

        default:
          return { success: false, error: `Unknown notification channel: ${channel}` };
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Notification step failed',
      };
    }
  },
};
